import { FC, useRef, useState } from 'react';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import ToolEvidenceCarouselItem from './components/ToolEvidenceCarouselItem/ToolEvidenceCarouselItem';
import ToolEvidenceModal from './components/ToolEvidenceModal/ToolEvidenceModal';
import { IToolEvidence, IToolEvidenceCarouselProps } from './toolEvidenceCarousel.types';
import { nudosTextcolor } from 'styles/styles.constants';
import './ToolEvidenceCarousel.scss';

/**
 * Componente de carrusel para mostrar evidencias fotográficas de la herramienta
 * @param evidences - Array de evidencias fotográficas
 * @param title - Título del carrusel
 * @param onEvidenceClick - Callback cuando se hace click en una evidencia
 * @param maxVisible - Número máximo de evidencias visibles antes de scroll
 * @param disabled - Indica si el componente debe mostrarse desactivado
 */
const ToolEvidenceCarousel: FC<IToolEvidenceCarouselProps> = ({
  evidences,
  title,
  onEvidenceClick,
  maxVisible = 5,
  showNavigationButtons,
  showEmptyState,
  disabled
}) => {
  const [selectedEvidence, setSelectedEvidence] = useState<IToolEvidence | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  const handleEvidenceClick = (evidence: IToolEvidence) => {
    if (disabled) return;
    setSelectedEvidence(evidence);
    setIsModalOpen(true);
    if (onEvidenceClick) {
      onEvidenceClick(evidence);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const scrollLeft = () => {
    if (disabled || !carouselRef.current) return;
    carouselRef?.current?.scrollBy({ left: -140, behavior: 'smooth' });
  };

  const scrollRight = () => {
    if (disabled || !carouselRef.current) return;
    carouselRef?.current?.scrollBy({ left: 140, behavior: 'smooth' });
  };

  const showNavigation = evidences?.length > maxVisible;

  if (showEmptyState)
    return (
      <div className='mt-3'>
        <div className='text-nudos-text-color text-sm'>Fotos de la herramienta (Evidencia)</div>
        <div className="text-sub-text-color text-sm">
          Sin fotos. Se agrega desde WMS si la herramienta está en bodega o en parada.
        </div>
      </div>
    );

  return (
    <div className={`toolEvidenceCarousel ${disabled ? 'disabled' : ''}`}>
      <div className="carouselHeader">
        <h3 className="carouselTitle">{title}</h3>
        <span className="evidenceCount">
          {evidences?.length} foto{evidences?.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="carouselContainer">
        {showNavigation && showNavigationButtons && (
          <button className="carouselNavButton left" onClick={scrollLeft} disabled={disabled}>
            <BordOneToneIcon variant="arrowLeft" customWidth="16px" stroke={nudosTextcolor} />
          </button>
        )}

        <div className="carouselTrack" ref={carouselRef}>
          {evidences?.map(evidence => (
            <ToolEvidenceCarouselItem key={evidence.id} evidence={evidence} onClick={handleEvidenceClick} />
          ))}
        </div>

        {showNavigation && showNavigationButtons && (
          <button className="carouselNavButton right" onClick={scrollRight} disabled={disabled}>
            <BordOneToneIcon variant="arrowRight" customWidth="16px" stroke="#383838" />
          </button>
        )}
      </div>

      <ToolEvidenceModal
        evidence={selectedEvidence}
        allEvidences={evidences}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default ToolEvidenceCarousel;
